<script lang="ts">
	//UI
	import { Card } from "@hitit/new_ui/src";
	import RefreshIcon from "@hitit/new_ui/src/SVG/RefreshIcon.svelte";
	import DefaultLoading from "@hitit/new_ui/src/SVG/DefaultLoading.svelte";
	//Utils
	import { createEventDispatcher } from "svelte";
	import { t } from "@hitit/hub/src/stores";
	//Types
	import type { PointTemplateDTO } from "@hitit/api/src/types";
	type Status = {
		channelId: string;
		content: string;
		id: string;
		templateCode: string;
		templateInspectionStatus: string;
		templateName: string;
		templateStatus: string;
		templateType: string;
	};
	type templateShowStatus = {
		show: boolean;
		logResponse: PointTemplateDTO;
	};
	//Props
	export let isLoading = false;
	export let templateDTOs: Status[] = [];
	//Constants
	const dispatch = createEventDispatcher();
	const templateDetailShowStatus: { [key: string]: templateShowStatus } = {};
	//Variables
	$: colHeader = [
		{ type: "kakao-channel", message: $t("mbr.point.kakao_channel") },
		{ type: "template-name", message: $t("mbr.point.template_name") },
		{ type: "template-code", message: $t("mbr.point.template_code") },
		{ type: "template-status", message: $t("mbr.point.template_status") },
		{
			type: "template-inspection-status",
			message: $t("mbr.point.template_inspection_status"),
		},
	];
	//Functions
	function refresh() {
		dispatch("kakaoTemplateRefresh");
	}

	function handleRow(dto: PointTemplateDTO) {
		if (templateDetailShowStatus[dto.id] === undefined) {
			templateDetailShowStatus[dto.id] = {
				show: true,
				logResponse: dto,
			};
		} else {
			const show = templateDetailShowStatus[dto.id].show;
			templateDetailShowStatus[dto.id].show = !show;
		}
	}
	//Do
</script>

<Card noPadding>
	<div class="card-content">
		<div class="title-wrapper">
			<h1 class="title">{$t("mbr.point.alarm_talk_template")}</h1>
			<div id="refresh" on:click={refresh}>
				<RefreshIcon loading={isLoading} />
			</div>
		</div>
		{#if isLoading}
			<div class="loading">
				<DefaultLoading />
			</div>
		{:else}
			<div class="head">
				{#each colHeader as col}
					<div class={`col-header ${col.type}`}>{col.message}</div>
				{/each}
			</div>

			<div class="body">
				{#each templateDTOs as template}
					<div
						class:showDetail={templateDetailShowStatus[template.id || ""]?.show}
						class="row"
						on:click={() => handleRow(template)}
					>
						<div class="item kakao-channel">{template.channelId}</div>
						<div class="item template-name">{template.templateName}</div>
						<div class="item template-code">{template.templateCode}</div>
						<div class="item template-status">{template.templateStatus}</div>
						<div class="item template-inspection-status">
							{template.templateInspectionStatus}
						</div>
					</div>
					{#if templateDetailShowStatus[template.id || ""]?.show}
						<div class="detail-box">
							<div class="detail-contents">
								<div class="text-box">
									{@html template.content}
								</div>
							</div>
						</div>
					{/if}
				{/each}
			</div>
		{/if}
	</div>
</Card>

<style lang="scss">
	@import "../../../variables.scss";
	.card-content {
		width: 100%;
		padding-bottom: 20px;

		.title-wrapper {
			display: flex;
			justify-content: space-between;

			#refresh {
				width: 27px;
				height: 27px;
				padding: 10px 10px 0px 0px;
				cursor: pointer;
			}
		}

		.loading {
			width: 30px;
			height: 30px;
			display: flex;
			justify-content: center;
			align-items: center;
			margin: 0 auto;
		}

		.title {
			margin: 0;
			font-size: 1.25rem;
			font-weight: 600;
			padding: 20px 10px;
			color: #46474b;
		}
		.head {
			display: none;
		}
		.row {
			display: flex;
			flex-direction: column;
			gap: 10px;
			padding: 10px 10px;
			cursor: pointer;

			&:not(.showDetail):not(:last-child)::after {
				content: "";
				display: block;
				width: calc(100%);
				height: 1px;
				background-color: #e6e7ec;
				order: 5;
				margin-bottom: 10px;
			}
		}
		.detail-box {
			padding: 20px 10px;
			background-color: #e6e7ed;
			.detail-contents {
				border-radius: 5px;
				padding: 10px 10px;
				background-color: white;
				@include appleAdjustTextForSize(16);

				.text-box {
					color: #46474b;
				}
			}
		}

		.item {
			letter-spacing: -0.65px;
			color: #46474b;
		}

		.kakao-channel {
			display: none;
		}
		.template-name {
			order: 2;
		}
		.template-code {
			order: 1;
			font-size: 1rem;
			font-weight: 500;
		}
		.template-status {
			order: 3;
		}
		.template-inspection-status {
			order: 4;
		}
	}

	@include DesktopSize {
		.card-content {
			padding-bottom: 30px;
			.title-wrapper {
				padding: 30px;
			}
			.title {
				font-size: 2.1875rem;
				padding: 0;
			}

			.head {
				display: flex;
				justify-content: space-between;
				padding: 0px 30px 0px 30px;
			}

			.row {
				justify-content: space-between;
				flex-direction: row;
				flex-wrap: wrap;
				gap: 0;
				padding: 20px 30px 0px 30px;
				&.showDetail {
					padding: 20px 30px 20px 30px;
				}
				&:not(.showDetail):not(:last-child) {
					border-bottom: none;
				}
				&:not(.showDetail):not(:last-child)::after {
					margin-top: 19px;
					margin-bottom: 0px;
				}
			}

			.detail-box {
				padding: 20px;
				.detail-contents {
					@include appleAdjustTextForSize(16, false);
				}
			}

			.col-header {
				display: flex;
				justify-content: center;
				text-align: center;
				letter-spacing: -0.8px;
				width: calc((100% - 60px) * 0.65);
				font-size: 1.25rem;
				color: #6d6f76;
			}

			.item {
				display: flex;
				justify-content: center;
				font-size: 1.25rem;
				letter-spacing: -0.8px;
				width: calc((100% - 60px) * 0.65);
			}

			.kakao-channel {
				display: flex;
				word-break: break-all;
				min-width: 150px;
				max-width: calc((100% - 60px) * (350 / 1594));
			}
			.template-name {
				word-break: break-all;
				min-width: 150px;
				max-width: calc((100% - 60px) * (350 / 1594));
				order: unset;
			}
			.template-code {
				min-width: 90px;
				max-width: calc((100% - 60px - 150px - 150px) * (260 / 1594));
				font-weight: 200;
				order: unset;
			}
			.template-status {
				min-width: 90px;
				max-width: calc((100% - 60px - 150px - 150px) * (260 / 1594));
				order: unset;
			}
			.template-inspection-status {
				min-width: 125px;
				max-width: calc((100% - 60px - 150px - 150px) * (260 / 1594));
				order: unset;
			}
		}
	}
</style>
